import { useState, useEffect, useCallback, useRef } from "react";
import {
  applyFleetControlRequest,
  pruneFleetControlRequests,
} from "../utils/fleet_control_request_store";
import SocketMqttBridge, {
  type BridgeConnectionSnapshot,
} from "../interface/socket_mqtt_bridge";
import type {
  ConnectionState,
  MergedVehicleInfo,
  VehiclePosition,
} from "../interface/auth_types";

interface UseMergedVehicleListResult {
  vehicleList: MergedVehicleInfo[];
  signalingStatus: ConnectionState;
  mqttStatus: ConnectionState;
  mqttRegisterState: BridgeConnectionSnapshot["mqttRegisterState"];
  signalingError: string | null;
  mqttError: string | null;
  mqttRegisterError: string | null;
  reload: () => Promise<void>;
}

const EMPTY_SNAPSHOT: BridgeConnectionSnapshot = {
  signalingState: "idle",
  mqttState: "idle",
  signalingError: null,
  mqttError: null,
  mqttRegisterState: "idle",
  mqttRegisterError: null,
};

export function useMergedVehicleList(
  enabled: boolean,
  bridge: SocketMqttBridge | null,
): UseMergedVehicleListResult {
  const [vehicleList, setVehicleList] = useState<MergedVehicleInfo[]>([]);
  const [signalingStatus, setSignalingStatus] = useState<ConnectionState>("idle");
  const [mqttStatus, setMqttStatus] = useState<ConnectionState>("idle");
  const [mqttRegisterState, setMqttRegisterState] =
    useState<BridgeConnectionSnapshot["mqttRegisterState"]>("idle");
  const [signalingError, setSignalingError] = useState<string | null>(null);
  const [mqttError, setMqttError] = useState<string | null>(null);
  const [mqttRegisterError, setMqttRegisterError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const previousTransportStateRef = useRef<{
    signalingState: ConnectionState;
    mqttState: ConnectionState;
  } | null>(null);

  const syncSnapshot = useCallback((snapshot: BridgeConnectionSnapshot) => {
    if (!mountedRef.current) {
      return;
    }

    setSignalingStatus(snapshot.signalingState);
    setMqttStatus(snapshot.mqttState);
    setMqttRegisterState(snapshot.mqttRegisterState);
    setSignalingError(snapshot.signalingError);
    setMqttError(snapshot.mqttError);
    setMqttRegisterError(snapshot.mqttRegisterError);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!bridge) {
      previousTransportStateRef.current = null;
      syncSnapshot(EMPTY_SNAPSHOT);
      return;
    }

    syncSnapshot(bridge.getConnectionSnapshot());
    return bridge.subscribeState(syncSnapshot);
  }, [bridge, syncSnapshot]);

  const reload = useCallback(async () => {
    if (!enabled || !bridge) {
      if (mountedRef.current) {
        setVehicleList([]);
        syncSnapshot(EMPTY_SNAPSHOT);
      }
      return;
    }

    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    syncSnapshot(bridge.getConnectionSnapshot());

    const nextVehicleList = await bridge.loadMergedVehicles();
    if (!mountedRef.current || requestSeq !== requestSeqRef.current) {
      return;
    }

    setVehicleList(nextVehicleList);
    syncSnapshot(bridge.getConnectionSnapshot());
  }, [enabled, bridge, syncSnapshot]);

  useEffect(() => {
    if (!enabled) {
      previousTransportStateRef.current = null;
      setVehicleList([]);
      syncSnapshot(EMPTY_SNAPSHOT);
      return;
    }
    void reload();
  }, [enabled, reload, syncSnapshot]);

  useEffect(() => {
    if (!enabled) {
      previousTransportStateRef.current = null;
      return;
    }

    const previous = previousTransportStateRef.current;
    const next = {
      signalingState: signalingStatus,
      mqttState: mqttStatus,
    };

    previousTransportStateRef.current = next;

    if (!previous) {
      return;
    }

    const signalingJustConnected =
      previous.signalingState !== "connected" &&
      signalingStatus === "connected";
    const mqttJustConnected =
      previous.mqttState !== "connected" && mqttStatus === "connected";

    if (signalingJustConnected || mqttJustConnected) {
      void reload();
    }
  }, [enabled, mqttStatus, reload, signalingStatus]);

  // 전차량 실시간 위치 구독 — MQTT 연결 + 차량 목록이 있을 때 활성.
  // 위치 업데이트가 들어오면 기존 목록의 position 만 패치(목록 재요청 없음).
  const vehicleIdKey = vehicleList
    .map((vehicle) => vehicle.vehicleId)
    .sort()
    .join(",");

  useEffect(() => {
    if (!enabled || !bridge || mqttStatus !== "connected" || !vehicleIdKey) {
      return;
    }

    const vehicleIds = vehicleIdKey.split(",").filter(Boolean);

    const applyPositions = (
      positions: ReadonlyMap<string, VehiclePosition>,
    ) => {
      if (!mountedRef.current) {
        return;
      }
      setVehicleList((current) =>
        current.map((vehicle) => {
          const position = positions.get(vehicle.vehicleId);
          if (position === vehicle.position) {
            return vehicle;
          }
          if (!position) {
            return vehicle.position ? { ...vehicle, position: undefined } : vehicle;
          }
          return { ...vehicle, position };
        }),
      );
    };

    const unsubscribe = bridge.subscribeFleetPositions(
      vehicleIds,
      applyPositions,
    );
    // 이미 캐시된 위치가 있으면 즉시 반영.
    applyPositions(bridge.getFleetPositions());

    // 같은 함대 토픽으로 제어권 요청도 올라온다 — 어느 차량이 요청했는지 알아야
    // 모니터링 화면에서 목록으로 띄울 수 있다.
    const unsubscribeRequests = bridge.onFleetControlRequest(
      (vehicleId, request) => {
        applyFleetControlRequest(vehicleId, request);
      },
    );
    // 재전송이 끊긴 요청은 스스로 사라져야 한다(차량이 꺼진 경우).
    const pruneTimer = window.setInterval(
      () => pruneFleetControlRequests(),
      5000,
    );

    return () => {
      unsubscribe();
      unsubscribeRequests();
      window.clearInterval(pruneTimer);
    };
  }, [enabled, bridge, mqttStatus, vehicleIdKey]);

  return {
    vehicleList,
    signalingStatus,
    mqttStatus,
    mqttRegisterState,
    signalingError,
    mqttError,
    mqttRegisterError,
    reload,
  };
}
